/**
 * Promise wrapper around FileReader.readAsDataURL
 * @module services/images/read-blob-as-data-url
 */

/**
 * Attach blob and FileReader error details to a file-read failure
 * @param {Error} error
 * @param {Blob} [blob]
 * @param {FileReader} [fileReader]
 * @returns {Error}
 */
export function enrichFileReadError (error, blob, fileReader) {
  const enrichedError = new Error(error.message)
  enrichedError.name = error.name
  if (blob) {
    enrichedError.fileSize = blob.size ?? null
    enrichedError.fileType = blob.type ?? null
  }
  if (fileReader?.error) {
    enrichedError.fileReaderErrorName = fileReader.error.name
    enrichedError.fileReaderErrorMessage = fileReader.error.message
  }
  return enrichedError
}

/**
 * True when a FileReader failure might succeed on another try
 * @param {Error} error
 * @returns {Boolean}
 */
export function isRetryableFileReadError (error) {
  if (!error?.message) {
    return false
  }
  return error.message !== 'No file blob provided'
}

/**
 * Read a Blob/File as a data URI string
 * @param {Blob} blob
 * @returns {Promise<String>}
 */
export function readBlobAsDataUrl (blob) {
  return new Promise((resolve, reject) => {
    if (!blob) {
      reject(new Error('No file blob provided'))
      return
    }
    const fileReader = new FileReader()
    fileReader.onload = (fileEvent) => {
      const result = fileEvent?.target?.result
      if (typeof result !== 'string' || !result) {
        reject(
          enrichFileReadError(new Error('FileReader returned an empty result'), blob, fileReader),
        )
        return
      }
      resolve(result)
    }
    fileReader.onerror = () => {
      reject(
        enrichFileReadError(new Error('FileReader failed to read the file'), blob, fileReader),
      )
    }
    fileReader.onabort = () => {
      reject(enrichFileReadError(new Error('FileReader was aborted'), blob, fileReader))
    }
    fileReader.readAsDataURL(blob)
  })
}
